import { computed, inject, Injectable, Signal, signal, WritableSignal } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { FylkerKommunerFull } from "../../models/kommunesok.model";
import { Observable, tap } from "rxjs";
import { AutocompleteOptionModel } from "@husbanken/designsystem-angular-forms";
import { KommuneEntry } from "../../pages/oppsummering/types/KommuneEntry";
import { map } from "rxjs/operators";
import { Enhet, Enheter } from "../../models/enhet";

@Injectable({
  providedIn: "root",
})
export class KommuneService {
  private http: HttpClient = inject(HttpClient);
  private kommunerUrl = "https://api.kartverket.no/kommuneinfo/v1/fylkerkommuner";
  private enheterUrl = "https://data.brreg.no/enhetsregisteret/api/enheter";

  private kommuner: Map<string, KommuneEntry> = new Map();

  private kommuneEnhet: WritableSignal<Enhet> = signal(undefined);
  readonly hjemmeside: Signal<string> = computed(() => {
    if (this.kommuneEnhet()?.hjemmeside) {
      return "https://" + this.kommuneEnhet()?.hjemmeside;
    }
    return undefined;
  });

  private _kommunenavn: WritableSignal<string> = signal(undefined);
  readonly kommunenavn: Signal<string> = this._kommunenavn.asReadonly();

  load(): Observable<AutocompleteOptionModel[]> {
    return this.http.get<FylkerKommunerFull[]>(this.kommunerUrl).pipe(
      map(data => this.toEntries(data)),
      tap((data: KommuneEntry[]) => {
        data.forEach(d => {
          this.kommuner.set(d.kommunenummer, d);
        });
      }),
    );
  }

  /**
   * Update "kommuneEnhet" to be the "Enhet" registered in Brønnøysundsregistrene
   * corresponding to kommunenummeret.
   */
  selectKommune(kommunenummer: string): void {
    if (kommunenummer === undefined || kommunenummer == null) {
      return;
    }

    const params: HttpParams = new HttpParams().set("organisasjonsform", "KOMM").set("kommunenummer", kommunenummer);
    this.http.get<Enheter>(this.enheterUrl, { params }).subscribe(enheter => {
      const enhet = enheter._embedded.enheter[0];
      this.kommuneEnhet.set(enhet);
      this._kommunenavn.set(this.kommuner.get(kommunenummer).kommunenavn);
    });
  }

  /**
   * Map API-response from Kartverket to entries used in <hb-felt-autocomplete>
   */
  private toEntries(fylker: FylkerKommunerFull[]): KommuneEntry[] {
    return fylker.flatMap(f =>
      f.kommuner.map(k => {
        return {
          fylkesnavn: f.fylkesnavn,
          kommunenavn: k.kommunenavnNorsk,
          kommunenummer: k.kommunenummer,
          tekst: k.kommunenavnNorsk,
          verdi: k.kommunenummer,
        };
      }),
    );
  }
}
